import { Chat } from "@components/mainInterface/chat/Chat";
import { Task, TaskType, isScheduleReviewTaskType } from "@common/types/task";
import { Fragment, useCallback, useMemo } from "react";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { hasSeparateChatPanel } from "@common/constants/taskTypeDefinitions";
import { useShouldAutoFocusTaskComposer } from "../taskComposerAutoFocus.context";
import { ScheduleReviewDisabledBody } from "./ScheduleReview/ScheduleReviewDisabledBody";
import { TaskBodyCommandIsland } from "./TaskBodyCommandIsland";
import { hasTaskBodyCommandIsland } from "./TaskBodyCommandIsland.lib";
import { TASK_BODY_BY_TASK_TYPE } from "./taskBodyByType";

interface TaskBodyPanelProps {
    selectedTask: Task;
}

export const TaskBodyPanel = ({ selectedTask }: TaskBodyPanelProps) => {
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { schedules: isSchedulesEnabled } = useTypedFlags();
    const shouldAutoFocusComposer = useShouldAutoFocusTaskComposer();

    const handleCreateTask = useCallback(() => {
        // Opens a create form the server can only refuse on save (PRO-11345).
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("createTask", {
            type: TaskType.QUESTION,
            initialValues: {
                assigneeId: selectedTask.assigneeId ?? undefined,
                priority: selectedTask.priority ?? undefined,
                dueDate: selectedTask.dueDate ?? undefined,
                entityIds: selectedTask.entityIds ?? undefined,
                groupId: selectedTask.groupId ?? undefined,
                closeId: selectedTask.closeId ?? undefined,
                closeStageId: selectedTask.closeStageId ?? undefined,
            },
        });
    }, [openOverlay, refuseIfViewOnly, selectedTask]);
    const customCommandIslandComponent = useMemo(
        () =>
            hasTaskBodyCommandIsland(selectedTask.type) ? (
                <TaskBodyCommandIsland selectedTask={selectedTask} />
            ) : null,
        [selectedTask],
    );

    const taskBody = useMemo(() => {
        if (
            isScheduleReviewTaskType(selectedTask.type) &&
            !isSchedulesEnabled
        ) {
            return <ScheduleReviewDisabledBody />;
        }
        const TaskBody = TASK_BODY_BY_TASK_TYPE[selectedTask.type];
        return TaskBody ? <TaskBody selectedTask={selectedTask} /> : null;
    }, [selectedTask, isSchedulesEnabled]);

    return (
        <>
            {!hasSeparateChatPanel(selectedTask.type) &&
            selectedTask.thread &&
            selectedTask.type !== TaskType.CONNECTION_BROKEN ? (
                <Chat
                    key={selectedTask.id}
                    selectedThread={selectedTask.thread}
                    setSelectedThread={() => {}}
                    hideHeader
                    autoFocusInput={shouldAutoFocusComposer}
                    isDisabled={!!customCommandIslandComponent}
                    customCommandIslandComponent={customCommandIslandComponent}
                    onCreateTask={handleCreateTask}
                />
            ) : (
                <Fragment key={selectedTask.id}>{taskBody}</Fragment>
            )}
        </>
    );
};
